import React, { useState } from "react";
import { Eye, eye, eyeOff, EyeOff } from "lucide-react";

function Register({ isOpen, onClose, onBackToLogin, screenid }) {
  const [userType, setUserType] = useState("resident");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Modal gate
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto hide-scrollbar p-8 relative border border-gray-100">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-800 text-2xl transition z-10"
          aria-label="Close"
        >
          &times;
        </button>

        {/* Header */}
        <div className="mb-7 text-center">
          <div className="mb-2">
            <span className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-teal-600 rounded-full text-white font-bold text-2xl">
              {userType === "resident" ? (
                "MV"
              ) : (
                <span className="text-lg">🌍</span>
              )}
            </span>
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900">
            Create Account
          </h2>
          <p className="text-gray-500 mt-2">
            Join and access your health dashboard instantly
          </p>
        </div>

        {/* Type Switch */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <button
            className={`px-6 py-3 rounded-full font-semibold transition-colors text-base
              ${
                userType === "resident"
                  ? "bg-blue-600 text-white shadow-lg"
                  : "bg-gray-100 text-gray-800 hover:bg-blue-50"
              }`}
            onClick={() => setUserType("resident")}
            type="button"
          >
            Maldives Resident
          </button>
          <button
            className={`px-6 py-3 rounded-full font-semibold transition-colors text-base
              ${
                userType === "tourist"
                  ? "bg-teal-600 text-white shadow-lg"
                  : "bg-gray-100 text-gray-800 hover:bg-teal-50"
              }`}
            onClick={() => setUserType("tourist")}
            type="button"
          >
            🌍 Tourist
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // handle submit logic
            console.log("Registration submitted for:", userType);
          }}
          className="space-y-6"
        >
          {/* Primary Details */}
          <fieldset className="border rounded-xl border-gray-200 px-6 py-5">
            <legend className="px-3 text-sm uppercase font-semibold text-gray-500 bg-white">
              📝 Primary Details
            </legend>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  required
                  placeholder="John"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                />
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  required
                  placeholder="Doe"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                />
              </div>
              <div>
                <label
                  htmlFor="mobile"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  id="mobile"
                  required
                  placeholder="+960 1234567"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  placeholder="your@email.com"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    required
                    placeholder="Choose a strong password"
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 pr-12 shadow-sm focus:border-gray-500 focus:ring-2 focus:ring-gray-200 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Confirm Password *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  required
                  placeholder="Repeat your password"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-gray-500 focus:ring-2 focus:ring-gray-200 transition"
                />
              </div>
            </div>
          </fieldset>

          {/* Resident-specific fields */}
          {userType === "resident" && (
            <fieldset className="border rounded-xl border-gray-200 px-6 py-5">
              <legend className="px-3 text-sm uppercase font-semibold text-gray-500 bg-white">
                🆔 Government ID Proof
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <div>
                  <label
                    htmlFor="govtIdType"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    ID Type *
                  </label>
                  <select
                    id="govtIdType"
                    required
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                  >
                    <option value="">Select ID type</option>
                    <option value="nid">National ID Card</option>
                    <option value="dl">Driving License</option>
                    <option value="passport">Passport</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="govtIdNumber"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    ID Number *
                  </label>
                  <input
                    type="text"
                    id="govtIdNumber"
                    required
                    placeholder="Enter your ID number"
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                  />
                </div>
                <div>
                  <label
                    htmlFor="govtInsuranceNumber"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Govt Insurance Number
                  </label>
                  <input
                    type="text"
                    id="govtInsuranceNumber"
                    placeholder="Enter your Insurance number"
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                  />
                </div>
                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload ID Proof *
                    <span className="text-xs text-gray-400 ml-2">
                      (jpg/png/pdf, max 5 MB)
                    </span>
                  </label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    required
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 cursor-pointer focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                  />
                </div>
              </div>
            </fieldset>
          )}

          {/* Tourist-specific fields */}
          {userType === "tourist" && (
            <>
              {/* Passport Details */}
              <fieldset className="border rounded-xl border-gray-200 px-6 py-5">
                <legend className="px-3 text-sm uppercase font-semibold text-gray-500 bg-white">
                  🛂 Passport Details
                </legend>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <div>
                    <label
                      htmlFor="passportNumber"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Passport Number *
                    </label>
                    <input
                      type="text"
                      id="passportNumber"
                      required
                      placeholder="Enter passport number"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="passportCountry"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Issuing Country *
                    </label>
                    <input
                      type="text"
                      id="passportCountry"
                      required
                      placeholder="Country of issuance"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="passportExpiry"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Expiry Date *
                    </label>
                    <input
                      type="date"
                      id="passportExpiry"
                      required
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition"
                    />
                  </div>
                  <div className="lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Passport Copy *
                      <span className="text-xs text-gray-400 ml-2">
                        (jpg/png/pdf, max 5 MB)
                      </span>
                    </label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      required
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 cursor-pointer focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition"
                    />
                  </div>
                </div>
              </fieldset>

              {/* Insurance Details */}
              <fieldset className="border rounded-xl border-gray-200 px-6 py-5">
                <legend className="px-3 text-sm uppercase font-semibold text-gray-500 bg-white">
                  🛡️ Travel Insurance Details
                </legend>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <div>
                    <label
                      htmlFor="insuranceProvider"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Insurance Provider *
                    </label>
                    <input
                      type="text"
                      id="insuranceProvider"
                      required
                      placeholder="e.g. Allianz Travel"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="policyNumber"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Policy Number *
                    </label>
                    <input
                      type="text"
                      id="policyNumber"
                      required
                      placeholder="Policy/Certificate number"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="coverageAmount"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Coverage Amount
                    </label>
                    <input
                      type="text"
                      id="coverageAmount"
                      placeholder="e.g. $100,000"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                    />
                  </div>
                  <div className="lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Insurance Document *
                      <span className="text-xs text-gray-400 ml-2">
                        (jpg/png/pdf, max 5 MB)
                      </span>
                    </label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      required
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 cursor-pointer focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition"
                    />
                  </div>
                </div>
              </fieldset>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white rounded-md py-2 font-medium hover:bg-blue-700 transition"
            >
              Create Account
            </button>
            {screenid === "register" && (
              <button
                type="button"
                onClick={onBackToLogin}
                className="flex-1 sm:flex-none border-2 border-gray-300 rounded-lg py-2 px-8 font-medium text-gray-700 hover:bg-gray-50 transition-all"
              >
                ← Back to Login
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default Register;
